<template>
  <div class="fe-feat-big mb-20">
    <div class="row g-0 align-items-center">
      <div class="col-md-6">
        <div class="fe-feat-big__thumb">
          <router-link :to="`/product-details/${product.id}`" class="fe-feat-big__img-link">
            <img :src="product.image" :alt="product.title" class="fe-feat-big__img" />
          </router-link>
          <span v-if="product.discount_percentage > 0" class="fe-feat-big__badge">
            -{{ product.discount_percentage }}%
          </span>
        </div>
      </div>
      <div class="col-md-6">
        <div class="fe-feat-big__body">
          <h6 class="fe-feat-big__title">
            <router-link :to="`/product-details/${product.id}`">{{ product.title }}</router-link>
          </h6>

          <div class="fe-feat-big__price-block">
            <span class="fe-feat-big__price">Rs. {{ formatPrice(product.price) }}</span>
            <div v-if="product.discount_percentage > 0" class="fe-feat-big__discount-line">
              <del class="fe-feat-big__was">Rs. {{ formatPrice(calculateOriginalPrice(product.price, product.discount_percentage)) }}</del>
              <span class="fe-feat-big__off">{{ product.discount_percentage }}% Off</span>
            </div>
          </div>

          <ul v-if="product.feature_lists && product.feature_lists.length" class="fe-feat-big__features">
            <li v-for="(feat, i) in product.feature_lists.slice(0, 4)" :key="i">
              <i class="fas fa-check"></i> {{ feat }}
            </li>
          </ul>

          <router-link :to="`/product-details/${product.id}`" class="fe-feat-big__cta">
            View More <i class="fas fa-arrow-right"></i>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useCurrency } from "@/composables/useCurrency"
import { calculateOriginalPrice } from "@/utils/utils"

export default {
  props: {
    product: {
      type: Object,
      required: true,
      default: () => ({
        id: null,
        title: "",
        price: 0,
        discount_percentage: 0,
        image: "",
        feature_lists: [],
      }),
    },
  },
  setup() {
    const { formatPrice } = useCurrency()
    return { formatPrice, calculateOriginalPrice }
  },
};
</script>

<style scoped>
.fe-feat-big {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 16px;
  overflow: hidden;
  height: 100%;
  transition: box-shadow 0.25s ease;
}
.fe-feat-big:hover {
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.07);
}

.fe-feat-big__thumb {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  min-height: 280px;
}

.fe-feat-big__img-link {
  display: flex;
  align-items: center;
  justify-content: center;
}

.fe-feat-big__img {
  max-width: 220px;
  max-height: 260px;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: transform 0.3s ease;
}
.fe-feat-big:hover .fe-feat-big__img {
  transform: scale(1.04);
}

.fe-feat-big__badge {
  position: absolute;
  top: 16px;
  left: 16px;
  background: #16a34a;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
}

.fe-feat-big__body {
  padding: 20px 24px 24px 10px;
}

.fe-feat-big__title {
  margin: 0 0 12px;
}
.fe-feat-big__title a {
  color: #1a1a1a !important;
  font-weight: 700;
  font-size: 15px;
  line-height: 1.35;
  text-transform: uppercase;
  text-decoration: none;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.fe-feat-big__title a:hover {
  color: var(--fe-accent, #fcbe00) !important;
}

.fe-feat-big__price-block {
  margin-bottom: 14px;
}

.fe-feat-big__price {
  font-size: 20px;
  font-weight: 800;
  color: #e65100;
  line-height: 1.2;
}

.fe-feat-big__discount-line {
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.fe-feat-big__was {
  font-size: 13px;
  font-weight: 500;
  color: #9ca3af;
}

.fe-feat-big__off {
  font-size: 12px;
  font-weight: 700;
  color: #e65100;
}

.fe-feat-big__features {
  list-style: none;
  padding: 0;
  margin: 0 0 18px;
}
.fe-feat-big__features li {
  font-size: 13px;
  color: #4b5563;
  padding: 3px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.fe-feat-big__features li i {
  color: #16a34a;
  font-size: 10px;
}

.fe-feat-big__cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(145deg, var(--fe-accent, #fcbe00) 0%, var(--fe-accent-hover, #e5ac00) 100%);
  color: #fff !important;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 10px 24px;
  border-radius: 999px;
  text-decoration: none;
  transition: filter 0.2s, transform 0.2s;
  box-shadow: 0 4px 14px rgba(252, 190, 0, 0.3);
}
.fe-feat-big__cta:hover {
  filter: brightness(1.05);
  transform: translateY(-1px);
}

@media (max-width: 767px) {
  .fe-feat-big__thumb {
    min-height: 200px;
    padding: 16px;
  }
  .fe-feat-big__img {
    max-width: 160px;
    max-height: 180px;
  }
  .fe-feat-big__body {
    padding: 16px;
  }
  .fe-feat-big__price {
    font-size: 18px;
  }
}
</style>
